import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  mapCurrentSubscriptionContract,
  mapInvoicesContract,
  mapUsageContract,
} from '../adapters/billing.adapter';
import { mapPlansContract } from '../adapters/plans.adapter';
import type {
  CancelRequestContract,
  DowngradeRequestContract,
  ReactivateRequestContract,
  SubscribeRequestContract,
  UpgradeRequestContract,
} from '../contracts/subscribe-request.contract';
import type {
  CancelResponseContract,
  SubscribeResponseContract,
} from '../contracts/subscribe-response.contract';
import {
  resolveBillingErrorKey,
  type BillingHttpError,
  type BillingUsageViolation,
} from '../models/billing-errors';
import type { InvoiceSummaryModel, SubscriptionModel, UsageModel } from '../models/billing.models';
import type { PlanCatalog } from '../models/plan-catalog.model';

const DEFAULT_INVOICES_TAKE = 12;

/**
 * Backend espera `billingCycle` e `paymentMethod` como int enums (Card 22.7 spec).
 * Internamente trabalhamos com PascalCase pra UX/legibilidade; aqui é o único ponto
 * de conversão pro wire format.
 *
 * Valores confirmados na spec:
 * - billingCycle: Monthly = 1, Annual = 2
 * - paymentMethod: CreditCard = 1, Pix = 2
 */
const BILLING_CYCLE_WIRE: Record<'Monthly' | 'Annual', number> = {
  Monthly: 1,
  Annual: 2,
};

const PAYMENT_METHOD_WIRE: Record<'CreditCard' | 'Pix', number> = {
  CreditCard: 1,
  Pix: 2,
};

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }
  const asciiMatch = /filename="?([^";]+)"?/i.exec(header);
  return asciiMatch?.[1]?.trim() ?? null;
}

@Injectable({ providedIn: 'root' })
export class BillingApiService {
  private readonly http = inject(HttpClient);

  async getPlans(): Promise<PlanCatalog[]> {
    try {
      const response = await firstValueFrom(this.http.get<unknown>('/api/billing/plans'));
      return mapPlansContract(response);
    } catch (error) {
      throw this.mapHttpError(error);
    }
  }

  async getCurrentSubscription(): Promise<SubscriptionModel | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>('/api/billing/current-subscription')
      );
      return mapCurrentSubscriptionContract(response as never);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) return null;
      throw this.mapHttpError(error);
    }
  }

  async getUsage(): Promise<UsageModel | null> {
    try {
      const response = await firstValueFrom(this.http.get<unknown>('/api/billing/usage'));
      return mapUsageContract(response as never);
    } catch (error) {
      // Empresa Canceled não tem plano ativo → backend devolve 404. Não é erro pra UI.
      if (error instanceof HttpErrorResponse && error.status === 404) return null;
      throw this.mapHttpError(error);
    }
  }

  async downloadInvoiceReceipt(invoiceId: string): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await firstValueFrom(
        this.http.get(`/api/billing/invoices/${encodeURIComponent(invoiceId)}/receipt`, {
          observe: 'response',
          responseType: 'blob',
        })
      );
      const blob = response.body ?? new Blob([], { type: 'application/pdf' });
      const filename = parseFilenameFromContentDisposition(
        response.headers.get('content-disposition')
      ) ?? `recibo-taskeo-${invoiceId}.pdf`;
      return { blob, filename };
    } catch (error) {
      throw await this.mapBlobHttpError(error);
    }
  }

  async getInvoices(take: number = DEFAULT_INVOICES_TAKE): Promise<InvoiceSummaryModel[]> {
    const params = new HttpParams().set('take', String(Math.max(1, Math.floor(take))));
    try {
      const response = await firstValueFrom(
        this.http.get<unknown>('/api/billing/invoices', { params })
      );
      if (Array.isArray(response)) return mapInvoicesContract(response);
      const items = (response as { items?: unknown })?.items;
      if (Array.isArray(items)) return mapInvoicesContract(items);
      return [];
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) return [];
      throw this.mapHttpError(error);
    }
  }

  async subscribe(request: SubscribeRequestContract): Promise<SubscribeResponseContract> {
    const wirePayload = {
      planSlug: request.planSlug,
      billingCycle: BILLING_CYCLE_WIRE[request.billingCycle],
      paymentMethod: PAYMENT_METHOD_WIRE[request.paymentMethod],
      ...(request.cardToken ? { cardToken: request.cardToken } : {}),
    };
    try {
      const response = await firstValueFrom(
        this.http.post<SubscribeResponseContract>('/api/billing/subscriptions', wirePayload)
      );
      return response ?? {};
    } catch (error) {
      throw this.mapHttpError(error);
    }
  }

  async upgrade(request: UpgradeRequestContract): Promise<SubscribeResponseContract> {
    try {
      const response = await firstValueFrom(
        this.http.post<SubscribeResponseContract>(
          '/api/billing/subscriptions/upgrade',
          request
        )
      );
      return response ?? {};
    } catch (error) {
      throw this.mapHttpError(error);
    }
  }

  async downgrade(request: DowngradeRequestContract): Promise<SubscribeResponseContract> {
    try {
      const response = await firstValueFrom(
        this.http.post<SubscribeResponseContract>(
          '/api/billing/subscriptions/downgrade',
          request
        )
      );
      return response ?? {};
    } catch (error) {
      throw this.mapHttpError(error);
    }
  }

  async cancel(request: CancelRequestContract): Promise<CancelResponseContract> {
    try {
      const response = await firstValueFrom(
        this.http.post<CancelResponseContract>(
          '/api/billing/subscriptions/cancel',
          request
        )
      );
      return response;
    } catch (error) {
      throw this.mapHttpError(error);
    }
  }

  async reactivate(request: ReactivateRequestContract): Promise<SubscribeResponseContract> {
    try {
      const response = await firstValueFrom(
        this.http.post<SubscribeResponseContract>(
          '/api/billing/subscriptions/reactivate',
          request
        )
      );
      return response ?? {};
    } catch (error) {
      throw this.mapHttpError(error);
    }
  }

  /**
   * Mapeia o body de erro (backend padronizado) pro `BillingHttpError`.
   *
   * Chaveia por `errorKey` (PascalCase com underscore). `error` (snake_case) é ignorado — é só
   * pra log interno do backend. Mensagens já vêm localizadas em `message` (Accept-Language),
   * então podem ser exibidas direto na UI.
   *
   * Campos extras (`resource`, `current`, `max`, `feature`, `planTier`, `upgradeTo`) são bônus
   * pra UX rica em erros 402 (Plan_Limit_Exceeded_* e Feature_Not_Available_*).
   */
  /**
   * Variante de mapHttpError pra endpoints com responseType: 'blob'. O Angular embrulha o body
   * de erro como Blob, então precisamos ler como texto e parsear como JSON antes de delegar.
   */
  private async mapBlobHttpError(error: unknown): Promise<BillingHttpError> {
    if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
      try {
        const text = await error.error.text();
        const parsed = text ? JSON.parse(text) : {};
        const synthetic = new HttpErrorResponse({
          error: parsed,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        });
        return this.mapHttpError(synthetic);
      } catch {
        return this.mapHttpError(error);
      }
    }
    return this.mapHttpError(error);
  }

  private mapHttpError(error: unknown): BillingHttpError {
    if (!(error instanceof HttpErrorResponse)) {
      return {
        status: 0,
        errorKey: 'Unknown',
        message: 'Erro inesperado.',
        violations: [],
      };
    }

    const body = (error.error ?? {}) as {
      error?: string;
      errorKey?: string;
      message?: string;
      resource?: string;
      current?: number;
      max?: number;
      feature?: string;
      planTier?: string;
      upgradeTo?: string;
      violations?: unknown;
      errors?: unknown;
    };

    const errorKey = resolveBillingErrorKey(body.errorKey ?? null);
    const message = body.message?.trim() || error.statusText || 'Erro ao processar operação.';

    const violations = this.extractViolations(body);

    return {
      status: error.status,
      errorKey,
      message,
      resource: typeof body.resource === 'string' ? body.resource : undefined,
      current: typeof body.current === 'number' ? body.current : undefined,
      max: typeof body.max === 'number' ? body.max : undefined,
      feature: typeof body.feature === 'string' ? body.feature : undefined,
      planTier: typeof body.planTier === 'string' ? body.planTier : undefined,
      upgradeTo: typeof body.upgradeTo === 'string' ? body.upgradeTo : undefined,
      violations,
    };
  }

  private extractViolations(body: { violations?: unknown; errors?: unknown }): BillingUsageViolation[] {
    const source = Array.isArray(body.violations)
      ? body.violations
      : Array.isArray(body.errors)
        ? body.errors
        : null;
    if (!source) return [];
    const out: BillingUsageViolation[] = [];
    for (const raw of source) {
      if (!raw || typeof raw !== 'object') continue;
      const entry = raw as { resource?: string; current?: number; limit?: number; max?: number };
      const resource = (entry.resource ?? '').toString().trim();
      if (!resource) continue;
      out.push({
        resource,
        current: typeof entry.current === 'number' ? entry.current : 0,
        // Backend pode mandar `max` (Card 20) ou `limit` (legado). Aceitar os dois.
        limit: typeof entry.max === 'number'
          ? entry.max
          : typeof entry.limit === 'number'
            ? entry.limit
            : 0,
      });
    }
    return out;
  }
}
